import { nanoid } from 'nanoid';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import i18n from '@sola/locales/index.ts';
import { AuthScenario } from '@sola/schema/dotcom/auth.ts';
import type { GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.ts';

import type {
  LocalAlertConfig,
  LocalAlertCreateProps,
} from '../types/localeAlerts.ts';

const ContactSupportAction = () => {
  const { t } = useTranslation('my');
  return (
    <LinkStyle inline noUnderline>
      <Link href={t('ctas.contactSupport.url')}>
        {t('ctas.contactSupport.text')}
      </Link>
    </LinkStyle>
  );
};

/**
 * Function for creating local alert data with an id.
 */
export const createLocalAlert = (
  alertProps: LocalAlertCreateProps,
): LocalAlertConfig => {
  return { id: nanoid(), ...alertProps };
};

export const createPaymentMethodSuccessAlert = (
  { creditCard, ach }: GraphQLSupporterPaymentMethod,
  actionTranslationKey: string,
): LocalAlertCreateProps => {
  const { t } = i18n;
  let lastFour = creditCard?.lastFour || '';
  let type = creditCard?.cardType || '';

  if (ach) {
    lastFour = ach.lastFourOfAccountNumber || '';
    type = t('my:payments.methodTypes.ach.short');
  }

  return {
    translationKey: 'payments.success',
    variant: 'positive',
    translationData: {
      lastFour,
      type,
      action: t(`my:${actionTranslationKey}`),
    },
  };
};

export const createPaymentMethodPartialSuccessAlert =
  (): LocalAlertCreateProps => {
    return {
      translationKey: 'payments.partialSuccess',
      variant: 'warning',
      dismissible: false,
      action: <ContactSupportAction />,
    };
  };

export const removePaymentMethodPartialSuccessAlert =
  (): LocalAlertCreateProps => {
    return {
      translationKey: 'payments.remove.partialSuccess',
      variant: 'negative',
      dismissible: true,
      action: <ContactSupportAction />,
    };
  };

export const removePaymentMethodFailureAlert = (): LocalAlertCreateProps => {
  return {
    translationKey: 'payments.remove.failure',
    variant: 'negative',
    dismissible: true,
    action: <ContactSupportAction />,
  };
};

export const createPaymentPlanSuccessAlert = (): LocalAlertCreateProps => {
  return {
    translationKey: 'commitments.detailsSheet.paymentPlanSuccess',
    variant: 'positive',
    closable: true,
  };
};

export const createSupportAlert = (translationKey: string) => {
  return createLocalAlert({
    translationKey,
    variant: 'negative',
    dismissible: true,
    action: <ContactSupportAction />,
  });
};

export const createCommitmentPaymentAddErrorAlert =
  (): LocalAlertCreateProps => {
    return {
      translationKey: 'makeAPayment.error',
      variant: 'negative',
      dismissible: true,
      action: <ContactSupportAction />,
    };
  };

export const createPartialDataAlert = (
  dataKey: string,
): LocalAlertCreateProps => {
  return {
    translationKey: `errors.partialData.${dataKey}`,
    variant: 'warning',
    action: <ContactSupportAction />,
  };
};

export const createPartialDataAlertMulti = (): LocalAlertCreateProps => {
  return {
    translationKey: `errors.partialData.base`,
    variant: 'warning',
    action: <ContactSupportAction />,
  };
};

export const createAddressUpdateSuccessAlert = (): LocalAlertCreateProps => ({
  translationKey: 'my-settings:addressUpdate.success',
  variant: 'positive',
  closable: true,
});

export const createContactInfoUpdateSuccessAlert =
  (): LocalAlertCreateProps => ({
    translationKey: 'my-settings:contactInfoUpdate.success',
    variant: 'positive',
    closable: true,
  });

export const createContactPreferenceUpdateSuccessAlert = (
  name: string,
): LocalAlertCreateProps => ({
  translationKey: 'preferences:contactPreferenceUpdate.success',
  translationContext: name ? 'name' : undefined,
  variant: 'positive',
  closable: true,
  ...(name ? { translationData: { name } } : {}),
});

export const createLetterWritingPreferencesUpdateSuccessAlert =
  (): LocalAlertCreateProps => ({
    translationKey: 'my-settings:letterWriting.success',
    variant: 'positive',
    closable: true,
  });

export const createUsernameUpdateSuccessAlert = (): LocalAlertCreateProps => ({
  translationKey: 'my-settings:usernameUpdate.success',
  variant: 'positive',
  closable: true,
});

export const createPasswordUpdateSuccessAlert = (): LocalAlertCreateProps => ({
  translationKey: 'my-settings:passwordUpdate.success',
  variant: 'positive',
  closable: true,
});

export const createPasswordUpdateErrorAlert = (): LocalAlertCreateProps => {
  const { t } = i18n;
  return {
    translationKey: 'my-settings:passwordUpdate.error',
    variant: 'negative',
    dismissible: true,
    action: (
      <a href={t('my:ctas.contactSupport.url')}>
        {t('my:ctas.contactSupport.text')}
      </a>
    ),
  };
};

export const SettingsErrorAlertMessage = ({
  i18nKey = 'my-settings:errors.generic.message',
}: {
  i18nKey?: string;
} = {}) => {
  const { t } = useTranslation(['my-settings', 'my', 'preferences']);
  return (
    <IntlBlock
      i18nKey={i18nKey}
      components={{
        SupportLink: (
          <LinkStyle inline noUnderline>
            <Link href={t('my:ctas.contactSupport.url')}>
              {t('my-settings:errors.generic.link')}
            </Link>
          </LinkStyle>
        ),
      }}
    />
  );
};

export const useUsernameUpdateErrorAlert = () => {
  const { t } = useTranslation(['my-settings']);
  return useCallback(
    (errorType?: string): React.ReactNode => {
      switch (errorType) {
        case AuthScenario.USERNAME_ALREADY_IN_USE: {
          return t('my-settings:usernameUpdate.errors.alreadyInUse');
        }
        case AuthScenario.TOO_MANY_ATTEMPTS: {
          return t('my-settings:usernameUpdate.errors.tooManyAttempts');
        }
        default: {
          return (
            <SettingsErrorAlertMessage i18nKey="my-settings:usernameUpdate.errors.internalError" />
          );
        }
      }
    },
    [t],
  );
};

type VolunteerAlertAction = 'cancel' | 'confirm';

const getVolunteerAlertTranslationData = (
  action: VolunteerAlertAction,
  count: number,
) => {
  const { t } = i18n;
  const translationKey = `volunteers:myEvents.alerts.actions.${action}`;

  return {
    action: t(`${translationKey}.past`, { count }),
    actionGerund: t(`${translationKey}.gerund`),
    actionNoun: t(`${translationKey}.noun`),
    count,
  };
};

export const createVolunteerActionSuccessAlert = (
  action: VolunteerAlertAction,
  count: number,
): LocalAlertCreateProps => ({
  translationKey: 'volunteers:myEvents.alerts.success',
  translationData: getVolunteerAlertTranslationData(action, count),
  variant: 'positive',
  closable: true,
});

export const createVolunteerActionWarningAlert = (
  action: VolunteerAlertAction,
  count: number,
): LocalAlertCreateProps => ({
  translationKey: 'volunteers:myEvents.alerts.warning',
  translationData: getVolunteerAlertTranslationData(action, count),
  variant: 'warning',
  closable: true,
});
